import { v4 as uuidv4 } from 'uuid';
import { createHttpTransport, OutboxSendError, WrongUserError, type Transport } from './http';
import { keyFor } from './keys';
import { classify, DROP_FALLBACK, isKeyReuse } from './policy';
import { isUnder, kindRegistry, type Registry } from './registry';
import type { OutboxStore } from './store';
import type {
  ClassifyInput, FileAccess, Identity, ItemPatch, ItemState, KindHandler, NewItemFile, OutboxItem, OutboxResponse, StepCtx, StepResult,
  Verdict,
} from './types';

/** Items expire after 7 days, the server's key retention on /crew: past it a resend could be applied twice. */
export const ITEM_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * A failed item (dropped by the server or expired) is deleted with its photos this long after the person was first shown it
 * (`seenAt`), whoever owns it, so nothing sits on the phone forever. The clock never starts at createdAt: an item that sat
 * for weeks must still be shown once. While nothing shows failed items (no list or subscribe consumer), nothing is deleted.
 */
export const FAILED_ITEM_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;
/** A clock this far behind the newest stamp in the store has gone backwards: no cleanup that pass. */
const CLOCK_SKEW_MS = 5 * 60 * 1000;
export const EXPIRED_MESSAGE = 'Not sent: it waited more than 7 days';

export interface OutboxOptions {
  store: OutboxStore;
  registry?: Registry;
  transport?: Transport;
  files?: FileAccess;
  /**
   * False when the store is not the durable one (the native database could not be opened, so items live in memory until the
   * app closes). Photos are then refused: their files would outlive a row that dies with the app. Default true.
   */
  durable?: boolean;
  /** Invalidates the queries a finished or dropped item touched (wired to the query client). */
  onRefetch?: (keys: readonly unknown[][]) => void;
  log?: (...args: unknown[]) => void;
}

export interface EnqueueInput<P = unknown> {
  kind: string;
  payload: P;
  /** Written through `FileAccess` before the item's row; the item then names them by path (`item.files`), never by bytes. */
  files?: NewItemFile[];
  /**
   * The only ordering guarantee. Items are tried oldest first, but one waiting on a retry, a 409 wait or an unregistered kind
   * lets later independent items overtake it. Every server precondition (arrive before leave, before photos before start) must
   * be declared here.
   */
  dependsOn?: string[];
  clientTime?: string;
  uploadId?: string;
}

export interface Outbox {
  /** Starts the one drain loop; calling it again does nothing. */
  start(): Promise<void>;
  /** False while items are kept only in memory (see `OutboxOptions.durable`): the app tells the user, and sign-out asks. */
  readonly durable: boolean;
  stop(): void;
  /** The signed-in user. Only their items are sent; signing (back) in releases their items held for sign-in. */
  setIdentity(identity: Identity | null): void;
  /** Retries run only in the foreground; coming back releases the items held while the account was read-only. */
  setForeground(foreground: boolean): void;
  /** The billing state changed: release the read-only holds once. */
  billingChanged(): void;
  /**
   * The network came back: items waiting out a retry delay after a lost connection (no answer, no status) are sent now instead of at
   * their timer (up to 30 s), then the loop runs. Items the server answered (a 5xx, a 409 wait) keep their own delay.
   */
  networkRestored(): Promise<void>;
  kick(): void;
  enqueue<P>(input: EnqueueInput<P>): Promise<OutboxItem>;
  /** The signed-in user's items, oldest first, including failed ones and finished ones with a notice. */
  list(): Promise<OutboxItem[]>;
  /** Items of `userId` not yet recorded by the server (anything but `done`): the sign-out prompt. */
  countPending(userId: number): Promise<number>;
  /** Sends a failed item again from the step it stopped at (an expired item stays failed). */
  retry(id: string): Promise<void>;
  /** Removes a failed item, or a finished one whose notice was read. Refused for the item in flight. */
  discard(id: string): Promise<void>;
  subscribe(listener: (items: OutboxItem[]) => void): () => void;
  /** Resolves when the loop has nothing left to do right now. */
  idle(): Promise<void>;
}

/** A handler sent something it must not: the item is dropped, whatever the handler then returns. */
class ClientBugError extends Error {}

const WAITING: ReadonlySet<ItemState> = new Set(['pending', 'sending', 'held_auth', 'held_read_only']);
/** Steps one item may advance in one drain: far above a 10 MiB upload's 40 chunks. */
const MAX_ADVANCES = 1000;
/** The wait before a drain that failed on storage runs again. */
const DRAIN_RETRY_MS = 30_000;
const SENDABLE: ReadonlySet<ItemState> = new Set(['pending', 'sending']); // `sending` at pick time = a crash mid-request
type Hold = 'held_auth' | 'held_read_only';

/** The largest file an item may carry: F6 compresses photos far below it, so only an uncompressed original reaches it. */
export const MAX_FILE_BYTES = 15 * 1024 * 1024;

const SHA256_HEX = /^[0-9a-f]{64}$/;

async function sha256Hex(data: Blob): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('SHA-256 is not available in this WebView: the photo cannot be checked, so it is not queued');
  const digest = new Uint8Array(await subtle.digest('SHA-256', await data.arrayBuffer()));
  return Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('');
}

const noFiles: FileAccess = {
  write: () => Promise.reject(new Error('This outbox has no file storage')),
  read: () => Promise.reject(new Error('This outbox has no file storage')),
  readRange: () => Promise.reject(new Error('This outbox has no file storage')),
  removeItemFiles: () => Promise.resolve(),
  sweep: () => Promise.resolve(),
};

/** The time of an ISO stamp, or undefined when it is missing or unreadable. */
function validMs(stamp: string | undefined): number | undefined {
  if (!stamp) return undefined;
  const ms = Date.parse(stamp);
  return Number.isNaN(ms) ? undefined : ms;
}

export function createOutbox(options: OutboxOptions): Outbox {
  const { store } = options;
  const registry = options.registry ?? kindRegistry;
  const transport = options.transport ?? createHttpTransport();
  const files = options.files ?? noFiles;
  const log = options.log ?? ((...args: unknown[]) => console.warn('[outbox]', ...args));

  let identity: Identity | null = null;
  let foreground = true;
  let started = false;
  let starting: Promise<void> | null = null;
  let stopped = false;
  let running: Promise<void> | null = null;
  let dirty = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: string | null = null;
  let lastCreatedMs = 0;
  const releases = new Set<Hold>();
  const listeners = new Set<(items: OutboxItem[]) => void>();
  // per item, the request each key was used for: two different requests under one key are a bug
  const usedKeys = new Map<string, Map<string, string>>();
  const unknownKinds = new Set<string>(); // logged once each

  const isMine = (item: OutboxItem) => !!identity && item.userId === identity.userId && item.orgId === identity.orgId;

  async function mine(): Promise<OutboxItem[]> {
    if (!identity) return [];
    const { orgId } = identity;
    return (await store.list({ userId: identity.userId })).filter((i) => i.orgId === orgId);
  }

  /** Stamps `seenAt` on the failed items about to be shown: from then on they may be cleaned up. Never blocks the listing. */
  async function surface(items: OutboxItem[]): Promise<OutboxItem[]> {
    const now = new Date().toISOString();
    const shown: OutboxItem[] = [];
    for (const item of items) {
      if (item.state !== 'failed' || validMs(item.seenAt) !== undefined) {
        shown.push(item);
        continue;
      }
      const seen = { ...item, seenAt: now };
      try {
        await store.put(seen);
        shown.push(seen);
      } catch (e) {
        log('could not mark an item as shown', item.id, e);
        shown.push(item);
      }
    }
    return shown;
  }

  async function notify() {
    if (listeners.size === 0) return;
    const items = await surface(await mine());
    listeners.forEach((listener) => listener(items));
  }

  async function save(item: OutboxItem) {
    await store.put(item);
    await notify();
  }

  // Once per outbox, before the first send and before any enqueue writes a file: deletes the files of items without a row
  // (a crash between the files and the row, either way). Nothing can be writing new files while it runs.
  let swept: Promise<void> | null = null;
  function sweepOnce(): Promise<void> {
    swept ??= (async () => {
      try {
        // every row's id, also of rows the store cannot parse: their photos stay until the row is dealt with
        await files.sweep(new Set(await store.ids()));
      } catch (e) {
        log('could not sweep the orphaned outbox files', e);
      }
    })();
    return swept;
  }

  /** Writes the item's files, then its row; resolves once both are written. On a failure, removes what it wrote and rejects. */
  /**
   * Refuses, before anything is written, files the server would refuse days later at finalize (a wrong digest) and files a
   * memory-only outbox could not keep.
   */
  async function checkFiles(newFiles: NewItemFile[]) {
    if (newFiles.length > 0 && options.durable === false) {
      throw new Error("This phone's outbox storage is unavailable: photos cannot be queued until the app is restarted");
    }
    for (const file of newFiles) {
      if (file.data.size > MAX_FILE_BYTES) {
        throw new Error(`Outbox file ${file.key} is too large (${file.data.size} bytes, at most ${MAX_FILE_BYTES})`);
      }
      if (!SHA256_HEX.test(file.sha256)) throw new Error(`Outbox file ${file.key}: the SHA-256 must be 64 lowercase hex characters`);
    }
    for (const file of newFiles) {
      if ((await sha256Hex(file.data)) !== file.sha256) throw new Error(`Outbox file ${file.key}: the SHA-256 does not match its bytes`);
    }
  }

  async function create(item: OutboxItem, newFiles: NewItemFile[]) {
    await checkFiles(newFiles);
    await sweepOnce();
    if (newFiles.length === 0) {
      await save(item);
      return;
    }
    try {
      const written: NonNullable<OutboxItem['files']> = [];
      for (const file of newFiles) {
        const path = await files.write(item.id, file.key, file.data);
        written.push({ key: file.key, path, bytes: file.data.size, sha256: file.sha256, ...(file.data.type && { type: file.data.type }) });
      }
      item.files = written;
      await store.put(item);
    } catch (e) {
      await files.removeItemFiles(item.id).catch((removeError) => log('could not remove the files of', item.id, removeError));
      throw e;
    }
    await notify();
  }

  async function forget(item: OutboxItem, notifyAfter = true) {
    usedKeys.delete(item.id);
    await store.remove(item.id); // the row first, then its files (F5: files are written before the row, deleted after it)
    await files.removeItemFiles(item.id).catch((e) => log('could not remove the files of', item.id, e));
    if (notifyAfter) await notify();
  }

  function refetch(handler: KindHandler<unknown>, item: OutboxItem) {
    try {
      options.onRefetch?.(handler.refetch(item));
    } catch (e) {
      log('refetch failed for', item.kind, e);
    }
  }

  // --- the one loop ---------------------------------------------------------------------------------------------------------

  function kick() {
    if (!started || stopped) return;
    dirty = true;
    if (running) return;
    running = (async () => {
      try {
        while (dirty) {
          dirty = false;
          await drain();
        }
      } catch (e) {
        // storage failed mid-drain (quota, an aborted or closed database): try again later rather than sleep until a kick
        log('drain failed', e);
        clearTimer();
        if (!stopped) timer = setTimeout(kick, DRAIN_RETRY_MS);
      } finally {
        running = null;
        if (dirty) kick();
      }
    })();
  }

  async function drain() {
    clearTimer();
    await expireOld();
    if (!identity) return;
    for (const hold of [...releases]) {
      releases.delete(hold);
      await release(hold);
    }
    const advances = new Map<string, number>();
    while (identity && foreground && !stopped) {
      const item = await nextItem();
      if (!item) break;
      const outcome = await runStep(item);
      if (outcome === 'blocked') break;
      if (outcome !== 'advanced') continue;
      // a handler that keeps advancing without ever settling is a bug, never a reason to spin
      const n = (advances.get(item.id) ?? 0) + 1;
      advances.set(item.id, n);
      if (n > MAX_ADVANCES) {
        const looping = await store.get(item.id);
        if (looping) {
          log('client bug: dropped, too many steps', item.kind, looping.step);
          await settle(looping, registry.get(item.kind)!, { kind: 'drop', message: DROP_FALLBACK });
        }
      }
    }
    await schedule();
  }

  async function nextItem(): Promise<OutboxItem | undefined> {
    const items = await mine();
    const waiting = new Set(items.filter((i) => WAITING.has(i.state)).map((i) => i.id));
    const now = Date.now();
    return items.find(
      (i) =>
        SENDABLE.has(i.state) &&
        (!i.nextAttemptAt || Date.parse(i.nextAttemptAt) <= now) &&
        registered(i) && // a kind whose feature has not registered yet waits for it
        // a failed or finished dependency no longer holds its dependents back: the server decides what they mean now
        !(i.dependsOn ?? []).some((d) => waiting.has(d)),
    );
  }

  function registered(item: OutboxItem): boolean {
    if (registry.get(item.kind)) return true;
    if (!unknownKinds.has(item.kind)) {
      unknownKinds.add(item.kind);
      log('items of an unregistered kind are waiting (until they expire)', item.kind);
    }
    return false;
  }

  /** One timer, for the soonest retry. None in the background, and none for held items: they wait for an event. */
  async function schedule() {
    clearTimer();
    if (!identity || !foreground || stopped) return;
    const now = Date.now();
    const due = (await mine())
      .filter((i) => SENDABLE.has(i.state) && i.nextAttemptAt)
      .map((i) => Date.parse(i.nextAttemptAt!))
      .filter((t) => t > now);
    if (due.length === 0) return;
    timer = setTimeout(kick, Math.min(...due) - now);
  }

  function clearTimer() {
    if (timer) clearTimeout(timer);
    timer = null;
  }

  async function expireOld() {
    const now = Date.now();
    const items = await store.list();
    // a clock that went backwards (or a stamp from the future) must not make anything look old
    const newest = Math.max(0, ...items.flatMap((i) => [validMs(i.createdAt), validMs(i.failedAt), validMs(i.seenAt)]).filter((n): n is number => n !== undefined));
    const clockBehind = now < newest - CLOCK_SKEW_MS;
    const cutoff = now - ITEM_TTL_MS;
    let removed = false;
    for (const item of items) {
      // one row that cannot be written or removed must not stop the rest, or the sends behind this pass
      try {
        if (item.state === 'failed') {
          const failedMs = validMs(item.failedAt);
          if (failedMs === undefined || failedMs > now + CLOCK_SKEW_MS) {
            // a row from before failedAt existed (or an unreadable or future stamp): its clock starts now, it is never deleted on sight
            await save({ ...item, failedAt: new Date(now).toISOString(), seenAt: undefined });
            continue;
          }
          const seenMs = validMs(item.seenAt);
          if (!clockBehind && seenMs !== undefined && seenMs <= now && now - seenMs > FAILED_ITEM_RETENTION_MS) {
            await forget(item, false);
            removed = true;
          }
          continue;
        }
        const created = Date.parse(item.createdAt);
        if (created >= cutoff) continue; // an unreadable createdAt (NaN) counts as expired
        if (WAITING.has(item.state)) {
          await save({ ...item, state: 'failed', failedAt: new Date(now).toISOString(), nextAttemptAt: undefined, lastError: { code: 'expired', message: EXPIRED_MESSAGE } });
        } else if (item.state === 'done') {
          await forget(item, false); // a notice nobody dismissed in 7 days
          removed = true;
        }
      } catch (e) {
        log('could not clean up an outbox item', item.id, e);
      }
    }
    if (removed) await notify().catch((e) => log('outbox listener failed', e));
  }

  async function release(hold: Hold) {
    for (const item of await mine()) {
      if (item.state === hold) await save({ ...item, state: 'pending', nextAttemptAt: undefined });
    }
  }

  async function holdAll(item: OutboxItem, hold: Hold) {
    for (const other of await mine()) {
      if (other.id !== item.id && SENDABLE.has(other.state)) await save({ ...other, state: hold });
    }
  }

  // --- one step of one item ------------------------------------------------------------------------------------------------

  async function runStep(picked: OutboxItem): Promise<'advanced' | 'settled' | 'blocked' | 'skipped'> {
    inFlight = picked.id; // before any await: a discard from now on is refused
    try {
      // re-read: the pick was a snapshot, and the item may have been removed or changed since
      const item = await store.get(picked.id);
      if (!item || !SENDABLE.has(item.state)) return 'skipped';
      const handler = registry.get(item.kind)!;
      item.state = 'sending'; // persisted with its step and index before the request goes out
      await save(item);
      return await stepOnce(item, handler);
    } finally {
      inFlight = null;
    }
  }

  async function stepOnce(item: OutboxItem, handler: KindHandler<unknown>): Promise<'advanced' | 'settled' | 'blocked'> {
    const view = structuredClone(item); // the handler's copy; only `ctx.update` changes the item
    const used = usedKeys.get(item.id) ?? new Map<string, string>();
    usedKeys.set(item.id, used);
    let last: OutboxResponse | undefined;
    let failure: ClassifyInput | undefined;
    let reuse: ClassifyInput | undefined;
    let bug: string | undefined;
    let blocked = false;

    const ctx: StepCtx = {
      files,
      async send(req) {
        if (reuse) throw new OutboxSendError(reuse); // nothing more goes out for this item
        if (!isMine(item)) {
          blocked = true;
          throw new WrongUserError();
        }
        if (!handler.paths.some((p) => isUnder(req.path, p))) {
          bug = `${req.method} ${req.path} is outside the paths of ${item.kind}`;
          throw new ClientBugError(bug);
        }
        if (req.method !== 'GET') {
          // a GET carries no key, so only writes can collide on one
          const key = keyFor(item, req.step, req.index);
          const request = `${req.method} ${req.path}`;
          const before = used.get(key);
          if (before !== undefined && before !== request) {
            bug = `${item.kind}: ${request} reuses the key of ${before} (${req.step}:${req.index ?? ''})`;
            throw new ClientBugError(bug);
          }
          used.set(key, request);
        }
        try {
          last = await transport.send(item, req);
          return last;
        } catch (e) {
          if (e instanceof WrongUserError) blocked = true;
          if (e instanceof OutboxSendError) {
            failure = e.res;
            if (isKeyReuse(e.res)) reuse = e.res;
          }
          throw e;
        }
      },
      async update(patch: ItemPatch) {
        if (reuse) throw new Error('A key was reused: this item is dropped, not restarted');
        const gen = item.uploadGen ?? 0;
        if (patch.uploadGen !== undefined && patch.uploadGen !== gen) {
          if (patch.uploadGen !== gen + 1) throw new Error(`uploadGen goes up by one: ${gen} -> ${patch.uploadGen}`);
          if (!patch.uploadId || patch.uploadId === item.uploadId) throw new Error('A new upload generation needs a new upload_id');
        } else if (patch.uploadId !== undefined && patch.uploadId !== item.uploadId) {
          throw new Error('A new upload_id needs a new upload generation');
        }
        Object.assign(item, patch);
        Object.assign(view, patch);
        await save(item);
      },
    };

    let result: StepResult | undefined;
    let verdict: Verdict | undefined;
    try {
      result = await handler.step(view, ctx);
    } catch (e) {
      if (e instanceof OutboxSendError) verdict = classify(e.res, item.attempts + 1);
      else if (!(e instanceof WrongUserError) && !(e instanceof ClientBugError)) {
        log('a step threw', item.kind, item.step, e);
        bug ??= e instanceof Error ? e.message : String(e);
      }
    }

    if (blocked) {
      // not sent: the item waits, unchanged, for its own user to be signed in
      await save({ ...item, state: 'pending' });
      return 'blocked';
    }
    if (reuse) {
      log('idempotency_key_reuse: dropped', { kind: item.kind, step: item.step, index: item.stepIndex, uploadGen: item.uploadGen ?? 0 });
      return settle(item, handler, { kind: 'drop', message: reuse.message || DROP_FALLBACK }, reuse);
    }
    if (bug) {
      log('client bug: dropped', bug);
      return settle(item, handler, { kind: 'drop', message: DROP_FALLBACK }, { message: bug });
    }
    if (result && 'verdict' in result) verdict = result.verdict;
    else if (result && 'done' in result) {
      const answered = last ? classify(last, 0) : undefined;
      verdict = { kind: 'done', notice: result.notice ?? (answered?.kind === 'done' ? answered.notice : undefined) };
    } else if (result && 'next' in result) {
      await save({
        ...item, step: result.next, stepIndex: result.index, state: 'pending', attempts: 0, nextAttemptAt: undefined, lastError: undefined,
      });
      return 'advanced';
    }
    return settle(item, handler, verdict ?? { kind: 'drop', message: DROP_FALLBACK }, failure);
  }

  async function settle(item: OutboxItem, handler: KindHandler<unknown>, verdict: Verdict, failure?: ClassifyInput) {
    const lastError = failure && {
      status: failure.status,
      code: failure.code,
      message: failure.message || (failure.network ? 'Network error' : DROP_FALLBACK),
    };
    switch (verdict.kind) {
      case 'done':
        if (verdict.notice) {
          usedKeys.delete(item.id);
          await save({ ...item, state: 'done', notice: verdict.notice, nextAttemptAt: undefined, lastError: undefined });
        } else {
          await forget(item);
        }
        refetch(handler, item);
        break;
      case 'retry':
      case 'wait':
        await save({
          ...item,
          state: 'pending',
          attempts: verdict.kind === 'retry' ? item.attempts + 1 : item.attempts,
          nextAttemptAt: new Date(Date.now() + verdict.delayMs).toISOString(),
          lastError,
        });
        break;
      case 'drop':
        usedKeys.delete(item.id);
        log('dropped', { kind: item.kind, step: item.step, uploadGen: item.uploadGen ?? 0, message: verdict.message });
        await save({ ...item, state: 'failed', failedAt: new Date().toISOString(), seenAt: undefined, nextAttemptAt: undefined, lastError: { ...lastError, message: verdict.message } });
        refetch(handler, item);
        break;
      case 'hold_auth':
      case 'hold_read_only': {
        const hold: Hold = verdict.kind === 'hold_auth' ? 'held_auth' : 'held_read_only';
        await save({ ...item, state: hold, nextAttemptAt: undefined, lastError });
        await holdAll(item, hold);
        break;
      }
    }
    return 'settled' as const;
  }

  // --- the API -------------------------------------------------------------------------------------------------------------

  return {
    durable: options.durable !== false,

    start() {
      // the loop starts only after the sweep: nothing is sent, and no file is written, while orphans are being deleted
      starting ??= sweepOnce().then(() => {
        started = true;
        // a fresh start counts as a resume: holds get one more try
        releases.add('held_auth');
        releases.add('held_read_only');
        kick();
      });
      return starting;
    },

    stop() {
      stopped = true;
      clearTimer();
    },

    setIdentity(next) {
      identity = next ? { ...next } : null;
      if (!identity) {
        clearTimer();
        return;
      }
      // every sign-in releases the sign-in holds, also the same user signing in again: one request each, re-held if still refused
      releases.add('held_auth');
      kick();
    },

    setForeground(next) {
      const resumed = next && !foreground;
      foreground = next;
      if (!next) {
        clearTimer();
        return;
      }
      if (resumed) releases.add('held_read_only');
      kick();
    },

    billingChanged() {
      releases.add('held_read_only');
      kick();
    },

    kick,

    async networkRestored() {
      try {
        for (const item of await mine()) {
          if (item.state === 'pending' && item.nextAttemptAt && item.lastError && item.lastError.status === undefined) {
            await store.put({ ...item, nextAttemptAt: undefined });
          }
        }
      } catch (e) {
        log('could not release the retries on reconnect', e);
      }
      kick();
    },

    async enqueue<P>(input: EnqueueInput<P>) {
      if (!identity) throw new Error('Nobody is signed in: nothing can be queued');
      const handler = registry.get(input.kind);
      if (!handler) throw new Error(`Unknown outbox kind ${input.kind}`);
      // strictly increasing, so the FIFO order is the enqueue order even within one millisecond
      const createdMs = Math.max(Date.now(), lastCreatedMs + 1);
      lastCreatedMs = createdMs;
      const item: OutboxItem = {
        id: uuidv4(),
        kind: input.kind,
        userId: identity.userId,
        orgId: identity.orgId,
        createdAt: new Date(createdMs).toISOString(),
        clientTime: input.clientTime ?? new Date().toISOString(),
        payload: input.payload,
        step: handler.firstStep,
        state: 'pending',
        attempts: 0,
        uploadGen: 0,
        ...(input.dependsOn?.length && { dependsOn: [...input.dependsOn] }),
        ...(input.uploadId && { uploadId: input.uploadId }),
      };
      await create(item, input.files ?? []);
      kick();
      return structuredClone(item);
    },

    async list() {
      return surface(await mine());
    },

    async countPending(userId) {
      return (await store.list({ userId })).filter((i) => i.state !== 'done').length;
    },

    async retry(id) {
      const item = await store.get(id);
      if (!item || !isMine(item) || item.state !== 'failed' || item.lastError?.code === 'expired') return;
      await save({ ...item, state: 'pending', attempts: 0, nextAttemptAt: undefined, lastError: undefined, failedAt: undefined, seenAt: undefined });
      kick();
    },

    async discard(id) {
      const item = await store.get(id);
      if (!item || !isMine(item) || inFlight === id || (item.state !== 'failed' && item.state !== 'done')) return;
      await forget(item);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async idle() {
      while (running) await running;
    },
  };
}
